import type { Widget, WidgetType } from '../types'
import { getDefaultGridSize, getWidgetConstraints } from '../widgetLayouts'
import { DASHBOARD_GRID_COLUMNS } from './dashboardGrid.constants'
import { compactWidgetLayouts, findFirstAvailableGridPosition } from './dashboardGrid.utils'

export function buildPresetWidgets(presetWidgets: Widget[], availableWidgets: Widget[]): Widget[] {
  const availableWidgetTypes = new Set(availableWidgets.map(({ type }) => type))
  const widgets = presetWidgets
    .filter(({ type }) => availableWidgetTypes.has(type))
    .map((widget) => ({
      ...widget,
      layout: widget.layout ? { ...widget.layout } : null,
      mobileLayout: widget.mobileLayout ? { ...widget.mobileLayout } : null,
    }))

  return compactWidgetLayouts(widgets)
}

export function buildSelectedWidgets(
  currentWidgets: Widget[],
  availableWidgets: Widget[],
  selectedWidgetTypes: WidgetType[]
): Widget[] {
  const selectedTypes = new Set(selectedWidgetTypes)
  const selectableTypes = new Set(availableWidgets.map(({ type }) => type))
  const widgets = currentWidgets.filter(
    ({ type }) => !selectableTypes.has(type) || selectedTypes.has(type)
  )
  const occupiedLayouts = widgets.flatMap(({ layout }) => (layout ? [layout] : []))

  availableWidgets.forEach((availableWidget) => {
    const isSelected = selectedTypes.has(availableWidget.type)
    const isAlreadyAdded = widgets.some(({ type }) => type === availableWidget.type)

    if (!isSelected || isAlreadyAdded) {
      return
    }

    const defaultSize = getDefaultGridSize(availableWidget.size)
    const position = findFirstAvailableGridPosition(
      occupiedLayouts,
      defaultSize,
      DASHBOARD_GRID_COLUMNS
    )
    const layout = getWidgetConstraints(
      { i: availableWidget.id, ...position, ...defaultSize },
      availableWidget.id,
      null,
      availableWidget.size
    )

    widgets.push({
      ...availableWidget,
      inUse: { desktop: true, mobile: true },
      layout,
      mobileLayout: null,
    })
    occupiedLayouts.push(layout)
  })

  return widgets
}
